/**
 * InvestigationWorkspacePageV2 — Cycle 101 Workspace-V2 shell.
 *
 * Sprint-2 shipped the substrate behind `ECHELON_WORKSPACE_V2`. Sprint-3
 * tail removed the flag gate and made V2 the unconditional workspace
 * (see `InvestigationWorkspacePage.tsx`).
 *
 * Layout (left → right):
 *   [InvestigationRail 280↔48] [canvas area] [SignalLedger 340]
 *
 * Canvas area vertical stack:
 *   [TopChrome 44]
 *   [ModeIdentityStrip]
 *   [ModuleCanvas — 12-col × 140px grid from sprint-3]
 *
 * Drawers (position: fixed, overlaid on top-right):
 *   - SignalDrawer (360) when ?signal= in URL
 *   - InvestigationDrawer (460) when ?investigation= in URL
 *   - GlobeTakeover placeholder when ?globe=true (sprint-4/5 wraps existing globe)
 *
 * Data wiring (sprint-3 tail): investigations come from `useInvestigationList`,
 * signals from `useOsintSignals`. Sprint-4 wires VesselTracker real data.
 * Sprint-5 wires FORGE patterns + WM analytical tiles.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// NavRail is mounted globally in AppLayout (cycle-102 sprint-1 Task 1.0); page-level mount removed.
import { TopChrome, type RegionId, type LiveRateReadout } from '../components/layout/TopChrome';
import {
  InvestigationRail,
  type InvestigationSummary as RailInvestigationSummary,
} from '../components/layout/InvestigationRail';
import { RailSuggestionsFooter } from '../components/workspace/RailSuggestionsFooter';
import { TweaksPanel } from '../components/workspace/TweaksPanel';
import { useWorkspaceTweaks } from '../hooks/useWorkspaceTweaks';
import {
  useCmdKExtraCommands,
  type CmdKCommand,
} from '../components/common/CmdKPalette';
import { ModeIdentityStrip } from '../components/workspace/ModeIdentityStrip';
import {
  SignalLedger,
  type SignalRow,
  type SignalSeverity,
} from '../components/workspace/SignalLedger';
import { MaritimeHealthBanner } from '../components/workspace/MaritimeHealthBanner';
import {
  getModeDef,
  type WorkspaceModeId,
} from '../components/workspace/modeRegistry';
import { ModeCanvasMount } from '../components/workspace/ModeCanvasMount';
import { ModulePicker } from '../components/workspace/ModulePicker';
import { ResetPresetModal } from '../components/workspace/ResetPresetModal';
import { StorageToastSystem } from '../components/workspace/StorageToastSystem';
import { ToastStack } from '../components/common/Toast';
import { GlobeTakeover } from '../components/workspace/GlobeTakeover';
import { SignalDrawerContainer } from '../components/workspace/SignalDrawerContainer';
import { InvestigationDrawerContainer } from '../components/workspace/InvestigationDrawerContainer';
import { deriveProximityCluster } from '../components/workspace/SignalDrawer';
import { useMaritimeAnomaly } from '../hooks/useMaritimeAnomaly';
import { useModeCanvasLayout } from '../hooks/useModeCanvasLayout';
import { useInvestigationList } from '../hooks/useInvestigation';
import { useOsintSignals, useOsintHealth } from '../hooks/useOsint';
import { useWorldMonitorLive } from '../hooks/useWorldMonitorLive';
import {
  useEII,
  useConvergenceHeatmap,
  useExternalIntelligence,
  useAuditSkipBeacon,
  useStructuralContext,
} from '../hooks/useIntel';
import { LineageTimelineStrip } from '../components/environment/LineageTimelineStrip';
import { EIIInspectorPanel } from '../components/workspace/intel/EIIInspectorPanel';
import { ConvergenceCellInspector } from '../components/workspace/intel/ConvergenceCellInspector';
import { FocalPointTopRail } from '../components/workspace/intel/FocalPointTopRail';
import { ExternalIntelligencePanel } from '../components/workspace/intel/ExternalIntelligencePanel';
import { StructuralContextPanel } from '../components/workspace/intel/StructuralContextPanel';
import type { ExternalIntelContext } from '../api/intelClient';
import type { InvestigationSummary as BackendInvestigationSummary } from '../types/investigation';
import type { OsintSignal } from '../api/osint';

// ── V1→V2 adapters (inline; sprint-4+ may extract to shared module) ─────

/** Map backend `InvestigationSummary` → V2 rail `InvestigationSummary`. */
export function adaptInvestigationForRail(
  inv: BackendInvestigationSummary
): RailInvestigationSummary {
  const statusRaw = (inv.status ?? '').toLowerCase();
  const status: RailInvestigationSummary['status'] =
    statusRaw === 'open' || statusRaw === 'active' || statusRaw === 'in_progress'
      ? 'open'
      : statusRaw === 'reviewing' || statusRaw === 'pending_review'
        ? 'reviewing'
        : statusRaw === 'ready' || statusRaw === 'complete' || statusRaw === 'promoted'
          ? 'ready'
          : statusRaw === 'closed' || statusRaw === 'dismissed'
            ? 'closed'
            : statusRaw === 'blocked' || statusRaw === 'failed'
              ? 'blocked'
              : 'open';

  // Best-effort headline: template_name > inquiry_class > id prefix.
  const headline =
    inv.template_name?.trim() ||
    inv.inquiry_class?.trim() ||
    `Investigation ${inv.id.slice(0, 8)}`;

  return {
    id: inv.id,
    status,
    headline,
    evidenceCount: inv.evidence_count,
    claimCount: inv.claim_count,
    driftCount: inv.drift_event_count,
    readiness: 0, // sprint-4+ derives from shadow_status + admission_disposition
    confidence: 0, // same
  };
}

/** Map backend `OsintSignal` → V2 ledger `SignalRow`.
 *
 * Fidelity derivation (cycle-101.1 sprint-3 beads `171x`): the backend
 * has no dedicated `fidelity` column today — raw arrivals are tagged by
 * their collector via `significance_reason: 'fidelity=raw'`
 * (convention used by the cycle-100 raw-path collectors and asserted by
 * `flywheel-validation.contract.test.tsx:89`). The adapter parses that
 * marker. Anything else is treated as `'composite'` so the chip only
 * fires when explicitly stamped — defensive default per PRD §Success-
 * criteria #5 Bullet 1 (visibly mark RAW, not the inverse). */
export function adaptOsintSignalForLedger(signal: OsintSignal): SignalRow {
  const sig = (signal.significance ?? '').toLowerCase();
  const severity: SignalSeverity =
    sig === 'critical'
      ? 'critical'
      : sig === 'high'
        ? 'high'
        : sig === 'moderate'
          ? 'moderate'
          : 'low';

  const headline =
    (typeof signal.normalised_data?.headline === 'string'
      ? (signal.normalised_data.headline as string)
      : typeof signal.normalised_data?.title === 'string'
        ? (signal.normalised_data.title as string)
        : null) ?? `${signal.signal_type} — ${signal.entity_ref ?? signal.source_id}`;

  const fidelity = deriveFidelity(signal);

  return {
    id: signal.id,
    receivedAt: signal.collected_at,
    domain: signal.source_group,
    headline,
    severity,
    requiresAttention: severity === 'critical' || severity === 'high',
    fidelity,
  };
}

function deriveFidelity(signal: OsintSignal): 'raw' | 'composite' {
  const reason = (signal.significance_reason ?? '').toLowerCase();
  if (reason.includes('fidelity=raw')) return 'raw';
  return 'composite';
}

// ── FR-W-2 live collection-rate derivation (cycle-105.8) ────────────────

/** Signals fetched for the live feed; also the saturation reference for the rate. */
const SIGNAL_FETCH_LIMIT = 50;
const LIVE_RATE_WINDOW_MIN = 5;

/**
 * FR-W-2 (Hermes-approved Option A): derive "signals collected per minute" from
 * the trailing 5-minute window of fetched signals.
 *
 * Uses `OsintSignal.collected_at` (Echelon ingestion time) — this measures *feed
 * throughput*, not external event frequency (see the LiveRate tooltip copy).
 * Returns null when no signal falls in the window or no timestamp is parseable,
 * so the caller omits the rate clause (honest sources-only fallback, NFR-4).
 * `isFloor` is true when the window is saturated by the fetch limit (the true
 * rate is ≥ the returned value) — shown as "≥K/min" rather than false precision.
 *
 * Pure (takes `nowMs`) so it is unit-testable with a fixed clock.
 */
export function deriveCollectedRate(
  signals: readonly OsintSignal[],
  fetchLimit: number,
  nowMs: number,
): { perMin: number; isFloor: boolean } | null {
  if (signals.length === 0) return null;
  const cutoff = nowMs - LIVE_RATE_WINDOW_MIN * 60_000;
  let inWindow = 0;
  let anyValidTimestamp = false;
  for (const s of signals) {
    const t = Date.parse(s.collected_at);
    if (Number.isNaN(t)) continue;
    anyValidTimestamp = true;
    if (t >= cutoff) inWindow += 1;
  }
  if (!anyValidTimestamp || inWindow === 0) return null;
  return { perMin: inWindow / LIVE_RATE_WINDOW_MIN, isFloor: inWindow >= fetchLimit };
}

export function InvestigationWorkspacePageV2() {
  // FR-S-1 (cycle-105.8): the CmdK provider + palette now live in AppLayout
  // (shell scope), so the page no longer mounts its own. The palette is shared
  // across /workspace, /environment, /market.
  return <WorkspaceV2Shell />;
}

function WorkspaceV2Shell() {
  const [activeMode, setActiveMode] = useState<WorkspaceModeId>('world');
  const [activeRegion, setActiveRegion] = useState<RegionId>('global');
  const [railCollapsed, setRailCollapsed] = useState(false);
  // FR-W-4 (cycle-105.8): de-orphaned workspace appearance tweaks. Drives the
  // module-style / panel-frame variant classes on the shell root below.
  const { tweaks } = useWorkspaceTweaks();
  // theme state removed: NavRail in AppLayout owns global theme toggle (cycle-102 sprint-1 Task 1.0).
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Canvas layout hook — shared between ModuleCanvas, ModeIdentityStrip
  // (module-count readout), FAB (add-placement), and RESET PRESET (Task 3.7).
  // Key insight: one hook instance per render so localStorage writes don't
  // race. WorkspaceModeId and ModeId are the same 5-enum by construction.
  const canvasLayout = useModeCanvasLayout(activeMode);
  const activeModeLabel = getModeDef(activeMode)?.label ?? String(activeMode).toUpperCase();

  // Live data: investigations + signals (sprint-3 tail data wiring).
  const { investigations: backendInvestigations } = useInvestigationList();
  const investigations = useMemo<readonly RailInvestigationSummary[]>(
    () => backendInvestigations.map(adaptInvestigationForRail),
    [backendInvestigations]
  );

  const signalsQuery = useOsintSignals({ limit: SIGNAL_FETCH_LIMIT });
  const signals = useMemo<readonly SignalRow[]>(() => {
    const raw = (signalsQuery.data?.signals ?? []) as readonly OsintSignal[];
    return raw.map(adaptOsintSignalForLedger);
  }, [signalsQuery.data]);

  // FR-W-2 (cycle-105.8): live collection-rate readout for TopChrome's SCOPE band.
  // "M sources live" from health; "K/min collected" derived from the signals
  // fetched above. Hidden on health error; sources-only when no rate is derivable.
  const healthQuery = useOsintHealth();
  const liveRateReadout = useMemo<LiveRateReadout>(() => {
    if (healthQuery.isError || !healthQuery.data) return { kind: 'hidden' };
    const { feeds_online, feeds_total } = healthQuery.data;
    const rate = deriveCollectedRate(
      (signalsQuery.data?.signals ?? []) as readonly OsintSignal[],
      SIGNAL_FETCH_LIMIT,
      Date.now(),
    );
    if (!rate) return { kind: 'sources', online: feeds_online, total: feeds_total };
    return {
      kind: 'full',
      online: feeds_online,
      total: feeds_total,
      perMin: rate.perMin,
      isFloor: rate.isFloor,
    };
  }, [healthQuery.isError, healthQuery.data, signalsQuery.data]);

  // Maritime-anomaly envelope for the SignalLedger health banner
  // (AC#6). Enabled when the active mode / region is maritime-relevant;
  // cycle-101-1 sprint-1 scope uses `hormuz` as the default chokepoint —
  // per-region routing lands in a follow-up cycle.
  const maritimeAnomaly = useMaritimeAnomaly({
    chokepoint: 'hormuz',
    enabled: activeMode === 'maritime' || activeRegion === 'mena',
  });

  // ── Drawer + takeover state (cycle-109 sprint-205 S3.6) ──────────────
  // In-memory state per AC-3.6.a + AC-3.6.c (no `useSearchParams` for
  // signal/investigation). Drawer ids and globe-open lift up from the
  // useDrawerStack pattern; signalWithCluster + ESC priority follow the
  // canonical handoff at workspace/app.jsx:174-291.
  const [globeOpen, setGlobeOpen] = useState(false);
  const [signalDrawerId, setSignalDrawerId] = useState<string | null>(null);
  const [investigationDrawerId, setInvestigationDrawerId] = useState<string | null>(null);
  // cmdK hook removed: NavRail in AppLayout owns ⌘K trigger; CmdKPalette mounts via CmdKProvider context.

  // ── Globe arrival contract (sprint-241 FR-S6.3) ──────────────────────
  // `/workspace?globe=1&domain=<d>` — the Environment DetailCard's
  // "Live signals →" deep-link. `globe=1` opens the takeover on mount;
  // `domain` is pinned as a best-effort layer-scope hint for GlobeTakeover.
  // Consumed ONCE: the params are CLEARED (replace, no history entry) after
  // the first apply — chosen over ignore-after-first-apply so closing and
  // reopening the globe in the same session doesn't re-scope, and a page
  // refresh doesn't replay the takeover.
  const [searchParams, setSearchParams] = useSearchParams();
  const [arrivalDomainScope, setArrivalDomainScope] = useState<string | null>(null);
  useEffect(() => {
    const globeParam = searchParams.get('globe');
    const domainParam = searchParams.get('domain');
    if (globeParam === null && domainParam === null) return;
    if (globeParam === '1') {
      setGlobeOpen(true);
      if (domainParam) setArrivalDomainScope(domainParam);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('globe');
    next.delete('domain');
    setSearchParams(next, { replace: true });
    // Mount-only by design: the arrival params exist only at navigation time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live signal corpus for the takeover + SignalDrawer cluster derivation.
  // React-Query dedupes when GlobeTakeover internally invokes the same hook.
  const { data: liveData } = useWorldMonitorLive();
  const liveSignals = liveData.signals;

  const selectedSignal = useMemo(
    () => liveSignals.find((s) => s.id === signalDrawerId) ?? null,
    [liveSignals, signalDrawerId]
  );
  const signalCluster = useMemo(
    () => (selectedSignal ? deriveProximityCluster(selectedSignal, liveSignals) : []),
    [selectedSignal, liveSignals]
  );

  const selectedInvestigation = useMemo(
    () => backendInvestigations.find((i) => i.id === investigationDrawerId) ?? null,
    [backendInvestigations, investigationDrawerId]
  );

  // FR-W-3 (cycle-105.8): active-investigation pip for TopChrome. Reuses the
  // adapted `headline` (template_name > inquiry_class > id-prefix) as the title.
  const activeInvestigationPip = useMemo(() => {
    if (!investigationDrawerId) return null;
    const adapted = investigations.find((i) => i.id === investigationDrawerId);
    return {
      code: `INV-${investigationDrawerId.slice(0, 8).toUpperCase()}`,
      title: adapted?.headline ?? `Investigation ${investigationDrawerId.slice(0, 8)}`,
    };
  }, [investigationDrawerId, investigations]);

  // ── Handlers (mirror design ref app.jsx:101-129) ────────────────────
  const openSignal = useCallback((id: string) => {
    setSignalDrawerId(id);
    setGlobeOpen((v) => v || true);
  }, []);
  const openInvestigation = useCallback((id: string) => {
    setInvestigationDrawerId(id);
    setGlobeOpen((v) => v || true);
  }, []);
  const openGlobe = useCallback(() => setGlobeOpen(true), []);
  const closeGlobe = useCallback(() => {
    setGlobeOpen(false);
    setSignalDrawerId(null);
    // keep investigation drawer id — reopening the globe restores it
  }, []);
  const closeSignal = useCallback(() => setSignalDrawerId(null), []);
  const closeInvestigation = useCallback(() => setInvestigationDrawerId(null), []);

  // FR-S-2 (cycle-105.8): register Workspace-surface commands into the
  // shell-mounted ⌘K palette so the extraCommands path is connected, not inert
  // (FR-H-1). Memoized so the registration effect doesn't thrash each render.
  const workspaceCommands = useMemo<CmdKCommand[]>(
    () => [
      {
        id: 'ws.toggle-rail',
        group: 'Workspace',
        label: railCollapsed ? 'Expand investigation rail' : 'Collapse investigation rail',
        keywords: ['rail', 'sidebar', 'investigations', 'collapse', 'expand'],
        action: () => setRailCollapsed((c) => !c),
      },
      {
        id: 'ws.toggle-globe',
        group: 'Workspace',
        label: globeOpen ? 'Close globe' : 'Open globe',
        keywords: ['globe', 'takeover', 'map', 'world'],
        action: () => (globeOpen ? closeGlobe() : openGlobe()),
      },
    ],
    [railCollapsed, globeOpen, closeGlobe, openGlobe],
  );
  useCmdKExtraCommands(workspaceCommands);

  // ── ESC priority (AC-3.6.b): Signal > Investigation > Takeover ──────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      const action = deriveEscDismissAction({ signalDrawerId, investigationDrawerId, globeOpen });
      if (action === 'signal') setSignalDrawerId(null);
      else if (action === 'investigation') setInvestigationDrawerId(null);
      else if (action === 'globe') setGlobeOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [signalDrawerId, investigationDrawerId, globeOpen]);

  return (
    <div
      className={`module-${tweaks.moduleStyle} panel-${tweaks.panelStyle}`}
      style={{
        display: 'flex',
        height: '100vh',
        background: 'var(--cd-bg)',
        color: 'var(--cd-fg)',
        fontFamily: 'var(--cd-font-sans)',
      }}
    >
      {/* NavRail mounted globally in AppLayout — page-level mount removed (cycle-102 sprint-1 Task 1.0). */}
      {/* FR-W-4 — workspace appearance tweaks (de-orphans useWorkspaceTweaks). */}
      <TweaksPanel />

      {/* Sprint-239: rail unmounts during the globe takeover so the takeover
          spans full width below the top bar (handoff precedent: SignalRail
          gated `!globeOpen`, app.jsx:218). Rail state (`railCollapsed`,
          activeId) is lifted page state, so unmount/remount is safe. */}
      {!globeOpen && (
        <InvestigationRail
          investigations={investigations}
          collapsed={railCollapsed}
          onToggleCollapse={() => setRailCollapsed((c) => !c)}
          onSelectInvestigation={openInvestigation}
          activeId={investigationDrawerId ?? undefined}
          footer={<RailSuggestionsFooter />}
        />
      )}

      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TopChrome
          activeMode={activeMode}
          onModeChange={setActiveMode}
          activeRegion={activeRegion}
          onRegionChange={setActiveRegion}
          liveRate={liveRateReadout}
          activeInvestigation={activeInvestigationPip}
          onClearInvestigation={closeInvestigation}
          onOpenGlobe={() => (globeOpen ? closeGlobe() : openGlobe())}
          globeOpen={globeOpen}
          canvasId="workspace-canvas-v2"
        />

        {/* Sprint-239: relative wrapper bounding the globe takeover BELOW
            TopChrome. The canonical handoff (app.jsx:174-258) mounts the
            takeover `absolute inset-0` inside the row div UNDER the top bar —
            the top bar (mode tabs + Close-globe button) stays visible and
            interactive during takeover, per the Workspace Globe View frame. */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Cycle-102 sprint-1 Task 1.4 — Workspace Zone 1 focal-points top rail. */}
          <FocalPointTopRail topN={10} />

          <ModeIdentityStrip
            mode={activeMode}
            moduleCount={canvasLayout.placements.length}
            investigationCount={investigations.length}
            onResetPreset={() => setShowResetConfirm(true)}
          />

          <ModeCanvasMount
            mode={activeMode}
            layout={canvasLayout}
            canvasId="workspace-canvas-v2"
          />

          {/* Bottom lineage timeline (sprint-237, sdd.md §4.2.2) — full-width
              strip below the module canvas region per Workspace 2 / Globe View
              frames. Cross-theatre corpus: environment runs with no theatre_id
              (environment_run_routes.py:155 optional param), newest-first via
              useLineageTimeline, limit 50 ([ASSUMPTION-S2]). */}
          <LineageTimelineStrip filters={{ limit: 50 }} />

          {/* Globe takeover + stacked drawers (cycle-109 sprint-205 S3.6;
           * remounted sprint-239). Mounted inside the relative wrapper so its
           * `absolute inset-0 z-10` covers exactly the below-top-bar region —
           * TopChrome remains visible/interactive during takeover (canonical
           * handoff app.jsx:174-258 "top bar stays" + the Workspace Globe View
           * frame). SignalDrawer + InvestigationDrawer mount as children so
           * they sit above the globe. InvestigationDrawer is rendered FIRST in
           * the children array so the SignalDrawer (higher zIndex on the
           * SignalDrawer aside) stacks on top per design ref app.jsx:241-256. */}
          {globeOpen && (
            <GlobeTakeover
              onSelectSignal={(s) => openSignal(s.id)}
              osintFeedOpen={!signalDrawerId && !investigationDrawerId}
              onOsintFeedClose={closeSignal}
              domainScope={arrivalDomainScope}
            >
              {selectedInvestigation && (
                <InvestigationDrawerContainer
                  inv={selectedInvestigation}
                  onClose={closeInvestigation}
                />
              )}
              {selectedSignal && (
                <SignalDrawerContainer
                  signal={selectedSignal}
                  cluster={signalCluster}
                  onClose={closeSignal}
                  onCreateSuccess={(invId) => {
                    openInvestigation(invId);
                    closeSignal();
                  }}
                />
              )}
            </GlobeTakeover>
          )}
        </div>
      </main>

      {/* Cycle-102 sprint-1 Task 1.2 — EII inspector right-rail. */}
      <EIIInspectorRail region={activeRegion === 'global' ? null : activeRegion} />

      {/* Cycle-105 sprint-1 Task 3 (3n8j.1) — External Intelligence panel.
          Sibling rail to EIIInspectorRail; context derived from the
          topmost open drawer (signal beats investigation).
          S3.7 FR-9 decision (cycle-109 sprint-205): hide-in-takeover via
          CSS — the composite-row data class doesn't fold into OsintFeed's
          atomic-signal stream. See grimoires/loa/NOTES.md Decision Log. */}
      <ExternalIntelligenceRail
        investigationId={investigationDrawerId}
        signalId={signalDrawerId}
        hidden={globeOpen}
      />

      {/* Sprint-239: the signal ledger is the handoff's optional right signal
          rail — designed in the DEFAULT view, explicitly absent during the
          takeover (app.jsx:218 `!globeOpen`); the takeover's right panel is
          its own inset OsintFeed. */}
      {!globeOpen && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <MaritimeHealthBanner envelope={maritimeAnomaly} />
          <SignalLedger
            signals={signals}
            onSelectSignal={openSignal}
            selectedId={signalDrawerId ?? undefined}
          />
        </div>
      )}

      {/* + FAB add-module flow (sprint-3 Task 3.6). Fixed-position at
          zIndex 20 — above the takeover's z-10 in the root stacking context,
          so it would float over the globe; gated `!globeOpen` (handoff
          precedent `hideFab={globeOpen}`). */}
      {!globeOpen && (
        <ModulePicker
          placements={canvasLayout.placements}
          onAddPlacement={canvasLayout.addPlacement}
        />
      )}

      {/* RESET PRESET confirm modal (sprint-3 Task 3.7). */}
      <ResetPresetModal
        open={showResetConfirm}
        modeLabel={activeModeLabel}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={() => canvasLayout.resetToPreset()}
      />

      {/* Storage toast controller (Cycle 101 sprint-3 Task 3.9, migrated to
          shared Toast primitive in cycle 101.1 sprint-3 Task 3.6). */}
      <StorageToastSystem />
      {/* Shared toast renderer — also drives ForgeLane dismiss + RESTORE. */}
      <ToastStack />
    </div>
  );
}

// ── EII inspector right-rail (cycle-102 sprint-1 Task 1.2) ────────────

type IntelTab = 'eii' | 'convergence';

function EIIInspectorRail({ region }: { region: string | null }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<IntelTab>('eii');
  const eii = useEII(region);
  const convergence = useConvergenceHeatmap({ enabled: open && tab === 'convergence' });

  const openOn = (next: IntelTab) => {
    setTab(next);
    setOpen(true);
  };

  return (
    <>
      {!open && (
        <div className="absolute right-72 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2">
          <RailToggleButton label="EII" onClick={() => openOn('eii')} />
          <RailToggleButton label="CNV" onClick={() => openOn('convergence')} />
        </div>
      )}
      {open && (
        <aside
          aria-label="Intel inspector panel"
          className="absolute right-72 top-12 z-30 w-[360px] max-h-[80vh]
            flex flex-col gap-2
            transition-transform duration-200 ease-out"
        >
          <div className="flex items-center justify-between gap-2 px-2">
            <div role="tablist" aria-label="Intel inspector tabs" className="flex gap-1">
              <IntelTabButton
                label="EII"
                active={tab === 'eii'}
                onClick={() => setTab('eii')}
              />
              <IntelTabButton
                label="Convergence"
                active={tab === 'convergence'}
                onClick={() => setTab('convergence')}
              />
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close intel inspector"
              className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider
                text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]"
            >
              ×
            </button>
          </div>
          {tab === 'eii' && (
            <EIIInspectorPanel
              region={region}
              data={eii.data}
              isLoading={eii.isLoading}
              error={eii.error}
              onRetry={() => eii.refetch()}
            />
          )}
          {tab === 'convergence' && (
            <div className="flex-1 min-h-[300px]">
              <ConvergenceCellInspector
                data={convergence.data}
                isLoading={convergence.isLoading}
                error={convergence.error}
              />
            </div>
          )}
        </aside>
      )}
    </>
  );
}

// ── External Intelligence rail (cycle-105 sprint-1 Task 3 / 3n8j.1) ────

function ExternalIntelligenceRail({
  investigationId,
  signalId,
  hidden = false,
}: {
  investigationId: string | null;
  signalId: string | null;
  /** AC-3.7.a: hide the rail (CSS `display: none`) when the globe takeover
   * is mounted. Preserves React state + the `useExternalIntelligence` query
   * + `useAuditSkipBeacon` effect so context tracking continues silently
   * regardless of visual state. */
  hidden?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Signal context is more specific than investigation context — when
  // both are present, the signal wins. If neither is open, context is
  // null and the hook's `enabled: context !== null` keeps the query
  // dormant (no network traffic, no audit-skip beacon fire).
  const context: ExternalIntelContext | null = useMemo(() => {
    if (signalId) return { kind: 'signal', id: signalId };
    if (investigationId) return { kind: 'investigation', id: investigationId };
    return null;
  }, [signalId, investigationId]);

  const query = useExternalIntelligence(context);

  // Structural Context panel (cycle-114 sprint-213 Task 3.5). NOT
  // investigation-scoped — surfaces the bounded in-scope structural priors
  // (CRI + cable-health) regardless of which drawer is open.
  const structuralQuery = useStructuralContext();

  // Audit-skip beacon — fires once per context-key whenever the hook
  // reports silent-skip (503/401). FR-1.5.c. Effect lives outside the
  // open/closed branch so the cohort row is recorded even when the
  // operator hasn't manually toggled the panel.
  useAuditSkipBeacon(context, query.data?.silentSkip ?? false);

  return (
    <div
      data-testid="external-intelligence-rail-wrapper"
      data-hidden={hidden ? 'true' : 'false'}
      style={{ display: hidden ? 'none' : 'contents' }}
    >
      {!open && (
        <div className="absolute right-72 top-[calc(50%+12rem)] -translate-y-1/2 z-20 flex flex-col gap-2">
          <RailToggleButton label="EXT" onClick={() => setOpen(true)} />
        </div>
      )}
      {open && (
        <aside
          aria-label="External intelligence inspector"
          className="absolute right-72 top-[calc(50%+8rem)] z-30 w-[360px] max-h-[60vh]
            flex flex-col gap-2
            transition-transform duration-200 ease-out"
        >
          <div className="flex items-center justify-end px-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close external intelligence inspector"
              className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider
                text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]"
            >
              ×
            </button>
          </div>
          {context === null ? (
            <section
              aria-label="External intelligence"
              data-echelon-scored="false"
              data-testid="external-intel-no-context"
              className="flex flex-col gap-2 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)]/85 backdrop-blur-md p-4"
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
                External intelligence
              </span>
              <p
                role="status"
                className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]/80 py-3"
              >
                open an investigation or signal to load external intelligence
              </p>
            </section>
          ) : (
            <ExternalIntelligencePanel
              context={context}
              data={query.data?.data ?? null}
              isLoading={query.isLoading}
              error={query.error}
            />
          )}
          {/* Structural Context panel (cycle-114 sprint-213 Task 3.5) —
              mounted in the same intel region, next to External Intelligence
              (SDD §10.4). Read-only, non-scored, not investigation-scoped. */}
          <StructuralContextPanel
            data={structuralQuery.data?.data ?? null}
            isLoading={structuralQuery.isLoading}
            error={structuralQuery.error}
          />
        </aside>
      )}
    </div>
  );
}

function RailToggleButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${label} inspector`}
      className="w-10 py-6 rounded-l-lg
        bg-[var(--e-bg-card)]/80 backdrop-blur-md
        border border-r-0 border-[var(--e-border-secondary)]
        text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]
        transition-colors cursor-pointer
        focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]"
      style={{ writingMode: 'vertical-rl' }}
    >
      <span className="text-[10px] font-mono tracking-wider" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}

function IntelTabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded-sm border
        focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]
        ${
          active
            ? 'border-[var(--e-purple-400)] text-[var(--e-text-primary)] bg-[var(--e-purple-400)]/10'
            : 'border-[var(--e-border-secondary)] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] hover:bg-[var(--e-bg-elevated)]'
        }`}
    >
      {label}
    </button>
  );
}

// Globe takeover placeholder removed in cycle-109 sprint-205 S3.6: the real
// `GlobeTakeover` component (S3.2) is now mounted directly by this page.
//
// `useEscapeHandler` helper removed in cycle-109 sprint-205 S3.6: the page
// owns the ESC priority (Signal > Investigation > Takeover) inline via a
// useEffect keyed on the three drawer slots — see AC-3.6.b.

// ── ESC priority helper (cycle-109 sprint-205 S3.6, exported for vitest) ─

export type EscDismissAction = 'signal' | 'investigation' | 'globe' | null;

/**
 * Pure priority resolver for AC-3.6.b. Returns the kind of the drawer
 * that ESC should close (or null if nothing is open). Priority is
 * Signal > Investigation > Takeover — signals are lightest-weight and
 * dismiss first; takeover dismisses last because closing it also
 * dismantles the surface that hosted the drawers.
 */
export function deriveEscDismissAction(state: {
  signalDrawerId: string | null;
  investigationDrawerId: string | null;
  globeOpen: boolean;
}): EscDismissAction {
  if (state.signalDrawerId) return 'signal';
  if (state.investigationDrawerId) return 'investigation';
  if (state.globeOpen) return 'globe';
  return null;
}
